/** @jsx jsx */
import { css, jsx } from '@emotion/react';

import { Fragment } from 'react';

import { FormattedMessage, FormattedNumber } from 'js/lib/coursera.react-intl';

import { Typography, Typography2 } from '@coursera/cds-core';
import { StarFilledIcon, ThumbsUpFilledIcon } from '@coursera/cds-icons';

import { ENROLLMENT_COUNT_THRESHOLD } from 'bundles/unified-description-page-common/constants';

import _t from 'i18n!nls/xdp';

type Props = {
  productRating?: number | null;
  totalReviewCount?: number | null;
  averageContentSatisfactionScore?: number | null;
  enrollmentCount?: number | null;
};

const styles = {
  wrapper: css`
    display: flex;
    align-items: center;
  `,
  iconContainer: () => css`
    padding: 0 var(--cds-spacing-100) 0 var(--cds-spacing-50);
    display: flex;
    align-items: center;
  `,
  divider: () => css`
    padding: 0 var(--cds-spacing-100);
  `,
  thumbsUpContainer: () => css`
    padding-right: var(--cds-spacing-50);
    display: flex;
    align-items: center;
  `,
};

const KeyInformationReviews = ({
  productRating,
  totalReviewCount,
  averageContentSatisfactionScore,
  enrollmentCount,
}: Props) => {
  const shouldDisplayEnrollmentCount = enrollmentCount && enrollmentCount > ENROLLMENT_COUNT_THRESHOLD;

  if (!productRating || !totalReviewCount) return null;

  return (
    <div css={styles.wrapper}>
      <Typography variant="h2semibold" component="div">
        {productRating.toFixed(1)}
      </Typography>
      <div css={styles.iconContainer}>
        <StarFilledIcon color="interactive" size="small" />
      </div>
      <Typography2 component="p" variant="bodySecondary" color="supportText">
        {_t('(#{totalReviewCount} reviews)', {
          totalReviewCount: totalReviewCount.toLocaleString(),
        })}
      </Typography2>
      {averageContentSatisfactionScore && (
        <Fragment>
          <div css={styles.divider}>|</div>
          <div css={styles.thumbsUpContainer}>
            <ThumbsUpFilledIcon color="interactive" />
          </div>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            {`${Math.round(averageContentSatisfactionScore)}%`}
          </Typography2>
        </Fragment>
      )}
      {shouldDisplayEnrollmentCount && (
        <Fragment>
          <div css={styles.divider}>|</div>
          <Typography2 component="p" variant="bodySecondary" color="supportText">
            <FormattedMessage message={_t('{count} enrolled')} count={<FormattedNumber value={enrollmentCount} />} />
          </Typography2>
        </Fragment>
      )}
    </div>
  );
};

export default KeyInformationReviews;
